/**
 * dview:ValidityViewer: a validity period as a time line with labelled ends ("From 1 Jan 2019",
 * "No end date") and today marked. With dview:show "state", the values are element states
 * (railml3:ElementState) and each gets its own line with its state.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { setting } from '@utils/viewShapes.utils';
import { localName } from '../linearLocation';
import { useDescribed } from './rinf';
import { formatDate, UriLink } from './values';
import type { Widget, WidgetContext } from './types';
import styles from './widgets.module.scss';

const TIME = 'http://www.w3.org/2006/time#';
const RAILML = 'http://ontology.railml.org/railml3#';
const YEAR = 365 * 24 * 3600 * 1000;

/** A date from a time instant IRI such as _timeinstant_2019-01-01, or a date literal. */
const dateOf = (value: string | undefined) => value?.match(/(\d{4}-\d{2}-\d{2})/)?.[1];

interface PeriodProps {
  begin: string;
  ends: string[];
  state?: ReactNode;
  pictures: boolean;
  /** The time:Instant behind each end date, so each listed date links to it. */
  endIris?: Record<string, string>;
  context?: WidgetContext;
}

function Period({ begin, ends, state, pictures, endIris, context }: PeriodProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const date = (value: string) => formatDate(value, i18n.language);
  const end = ends.length === 1 ? ends[0] : undefined;
  const from = new Date(begin).getTime();
  const to = end ? new Date(end).getTime() : null;
  const now = Date.now();
  const lo = Math.min(from, now) - YEAR / 2;
  const hi = Math.max(to ?? now, now) + YEAR;
  const pct = (time: number) => ((time - lo) / (hi - lo)) * 100;
  const expired = to !== null && to < now;
  const status = expired ? t('entity.template.validity.ended') : from > now ? t('entity.template.validity.future') : t('entity.template.validity.current');

  return (
    <span className={styles.validity}>
      <span className={styles.validityText}>
        {state && <b>{state} · </b>}
        {status}
        {ends.length > 1 && <span className={styles.validityWarn}> · {t('entity.template.validity.manyEnds', { count: ends.length })}</span>}
      </span>
      {pictures && (
        <span className={styles.validityLine} role="img" aria-label={`${date(begin)} → ${end ? date(end) : t('entity.template.validity.noEnd')}`}>
          <span className={styles.validityEnd}>{t('entity.template.validity.from', { date: date(begin) })}</span>
          <span className={styles.validityBar} aria-hidden="true">
            <span
              className={styles.validityPeriod}
              data-expired={expired || undefined}
              data-open={to === null || undefined}
              style={{ left: `${pct(from)}%`, width: `${pct(to ?? hi) - pct(from)}%` }}
            />
            <span className={styles.validityToday} style={{ left: `${pct(now)}%` }}>
              <span>{t('entity.template.validity.today')}</span>
            </span>
          </span>
          <span className={styles.validityEnd}>
            {end ? t('entity.template.validity.to', { date: date(end) }) : ends.length > 1 ? '?' : t('entity.template.validity.noEnd')}
          </span>
        </span>
      )}
      {ends.length > 1 && (
        <details className={styles.validityEnds}>
          <summary>{t('entity.template.validity.showEnds', { count: ends.length })}</summary>
          <ul>
            {ends.map((value) => (
              <li key={value}>
                {endIris?.[value] && context ? (
                  <UriLink uri={endIris[value]} context={context}>
                    {date(value)}
                  </UriLink>
                ) : (
                  date(value)
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </span>
  );
}

const States = ({ iris, pictures, context }: { iris: string[]; pictures: boolean; context: WidgetContext }): JSX.Element => {
  const nodes = useDescribed(iris);
  return (
    <span className={styles.validityList}>
      {iris.map((iri) => {
        const node = nodes[iri];
        const objects = (predicate: string) => node?.find((property) => property.predicate === predicate)?.objects.map((object) => object.value) ?? [];
        const begin = dateOf(objects(`${TIME}hasBeginning`)[0]);
        if (!begin) return <span key={iri}>{node ? localName(iri) : '…'}</span>;
        const endIris = Object.fromEntries(
          objects(`${TIME}hasEnd`)
            .map((instant) => [dateOf(instant), instant] as const)
            .filter(([value]) => value && !value.startsWith('9999'))
        ) as Record<string, string>;
        const ends = Object.keys(endIris);
        const state = objects(`${RAILML}hasValue`).map(localName)[0];
        return (
          <Period
            key={iri}
            begin={begin}
            ends={[...new Set(ends)].sort()}
            state={state && <UriLink uri={iri} context={context}>{state}</UriLink>}
            pictures={pictures}
            endIris={endIris}
            context={context}
          />
        );
      })}
    </span>
  );
};

export const ValidityViewer: Widget = ({ property, values, context }) => {
  if (setting(property, 'show') === 'state') {
    const iris = values.filter((value) => value.type === 'uri').map((value) => value.value);
    return iris.length > 0 ? <States iris={iris} pictures={context.pictures ?? true} context={context} /> : null;
  }
  const begin = dateOf(values[0]?.value) ?? values[0]?.value;
  if (!begin) return null;
  const endRow = context.model?.rows[setting(property, 'end') ?? ''];
  const end = endRow ? context.model?.values[endRow.id]?.[0]?.value : undefined;
  return <Period begin={begin} ends={end && !end.startsWith('9999') ? [end] : []} pictures={context.pictures ?? true} />;
};
